import { X, Zap, Plus, Pencil, Trash2, Eye, Send, Search } from 'lucide-react'
import { useState, useCallback, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { ScrollArea } from '@/common/ui/scroll-area'
import { Skeleton } from '@/common/ui/skeleton'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from '@/common/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'

import {
	useQueryQuickResponses,
	useMutationCreateQuickResponse,
	useMutationUpdateQuickResponse,
	useMutationDeleteQuickResponse,
} from '../../api/hooks'
import {
	MessageListEditor,
	MessageListPreview,
} from '../../pages/ChatSettingsPage/components'
import {
	messageContentSchema,
	validateMessageContent,
	type MessageContentInput,
} from '../../pages/ChatSettingsPage/types'

import { ChatMessageType, type components } from '@/schema'

type QuickResponseResponse = components['schemas']['QuickResponseResponse']

// Type for modified quick response with edited messages
type ModifiedQuickResponse = Omit<QuickResponseResponse, 'messages'> & {
	messages: Array<{
		id: string
		order: number
		messageType: string
		text?: string | null
		stickerPackageId?: string | null
		stickerId?: string | null
		imageUrl?: string | null
		videoUrl?: string | null
		videoPreviewUrl?: string | null
		audioUrl?: string | null
		audioDuration?: number | null
		fileUrl?: string | null
		fileName?: string | null
		locationTitle?: string | null
		locationAddress?: string | null
		locationLatitude?: number | null
		locationLongitude?: number | null
		flexAltText?: string | null
		flexContents?: Record<string, unknown> | null
	}>
}

// ============================================================================
// Quick Responses Panel (for chat input - show title + preview button)
// ============================================================================

interface QuickResponsesPanelProps {
	onSelect: (response: QuickResponseResponse) => void
	onClose: () => void
}

export function QuickResponsesPanel({
	onSelect,
	onClose,
}: QuickResponsesPanelProps) {
	const { data: responses, isLoading } = useQueryQuickResponses()
	const [previewResponse, setPreviewResponse] =
		useState<QuickResponseResponse | null>(null)
	const [editResponse, setEditResponse] =
		useState<QuickResponseResponse | null>(null)
	const [searchQuery, setSearchQuery] = useState('')

	// Filter responses based on search query
	const filteredResponses = useMemo(() => {
		if (!responses) return []
		if (!searchQuery.trim()) return responses

		const query = searchQuery.toLowerCase().trim()
		return responses.filter(
			(response) =>
				response.name.toLowerCase().includes(query) ||
				response.messages.some(
					(msg) => msg.text && msg.text.toLowerCase().includes(query),
				),
		)
	}, [responses, searchQuery])

	// Handle selecting a quick response - open edit dialog instead of direct send
	const handleSelectResponse = useCallback(
		(response: QuickResponseResponse) => {
			setEditResponse(response)
		},
		[],
	)

	// Handle sending edited messages
	const handleSendEdited = useCallback(
		(modifiedResponse: ModifiedQuickResponse) => {
			onSelect(modifiedResponse as QuickResponseResponse)
			setEditResponse(null)
		},
		[onSelect],
	)

	return (
		<div className="flex h-[70vh] flex-col border-t bg-muted/30">
			{/* Header */}
			<div className="flex shrink-0 items-center justify-between border-b px-4 py-2">
				<div className="flex items-center gap-2">
					<Zap className="h-4 w-4 text-primary" />
					<span className="text-sm font-medium">ข้อความด่วน</span>
				</div>
				<Button
					variant="ghost"
					size="icon"
					className="h-6 w-6"
					onClick={onClose}
				>
					<X className="h-4 w-4" />
				</Button>
			</div>

			{/* Search Input */}
			{!isLoading && responses && responses.length > 0 && (
				<div className="shrink-0 border-b px-2 py-2">
					<div className="relative">
						<Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							type="text"
							placeholder="ค้นหาข้อความด่วน..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="h-8 pl-8 text-sm"
						/>
					</div>
				</div>
			)}

			{/* Content */}
			<div className="min-h-0 flex-1 overflow-y-auto">
				{isLoading ? (
					<div className="space-y-2 p-2">
						{Array.from({ length: 3 }).map((_, i) => (
							<Skeleton key={i} className="h-10 w-full" />
						))}
					</div>
				) : !responses || responses.length === 0 ? (
					<div className="p-4 text-center text-sm text-muted-foreground">
						ยังไม่มีข้อความด่วน
					</div>
				) : filteredResponses.length === 0 ? (
					<div className="p-4 text-center text-sm text-muted-foreground">
						ไม่พบข้อความด่วนที่ค้นหา
					</div>
				) : (
					<div className="grid grid-cols-2 gap-2 p-2 sm:grid-cols-3 md:grid-cols-4">
						{filteredResponses.map((response) => (
							<div
								key={response.id}
								className="flex items-center gap-1 rounded-lg border bg-background"
							>
								<button
									onClick={() => handleSelectResponse(response)}
									className="flex-1 px-3 py-2 text-left text-sm transition-colors hover:bg-muted"
								>
									<span className="font-medium">{response.name}</span>
									<p className="mt-0.5 text-xs text-muted-foreground">
										{response.messages.length} ข้อความ
									</p>
								</button>
								<Button
									variant="ghost"
									size="icon"
									className="h-8 w-8 shrink-0"
									onClick={(e) => {
										e.stopPropagation()
										setPreviewResponse(response)
									}}
								>
									<Eye className="h-4 w-4 text-muted-foreground" />
								</Button>
							</div>
						))}
					</div>
				)}
			</div>

			{/* Preview Dialog (read-only) */}
			<QuickResponsePreviewDialog
				response={previewResponse}
				open={!!previewResponse}
				onOpenChange={(open) => !open && setPreviewResponse(null)}
				onSend={() => {
					if (previewResponse) {
						// Open edit dialog instead of direct send
						setEditResponse(previewResponse)
						setPreviewResponse(null)
					}
				}}
			/>

			{/* Edit Dialog (editable before send) */}
			<QuickResponseEditDialog
				response={editResponse}
				open={!!editResponse}
				onOpenChange={(open) => !open && setEditResponse(null)}
				onSend={handleSendEdited}
			/>
		</div>
	)
}

// ============================================================================
// Preview Dialog Component
// ============================================================================

interface QuickResponsePreviewDialogProps {
	response: QuickResponseResponse | null
	open: boolean
	onOpenChange: (open: boolean) => void
	onSend?: () => void
}

function QuickResponsePreviewDialog({
	response,
	open,
	onOpenChange,
	onSend,
}: QuickResponsePreviewDialogProps) {
	if (!response) return null

	// Transform response messages to MessageContentInput format
	const messages: MessageContentInput[] = response.messages.map((msg) => ({
		id: msg.id,
		order: msg.order,
		messageType: msg.messageType as ChatMessageType,
		text: msg.text,
		stickerPackageId: msg.stickerPackageId,
		stickerId: msg.stickerId,
		imageUrl: msg.imageUrl,
		videoUrl: msg.videoUrl,
		videoPreviewUrl: msg.videoPreviewUrl,
		audioUrl: msg.audioUrl,
		audioDuration: msg.audioDuration,
		fileUrl: msg.fileUrl,
		fileName: msg.fileName,
		locationTitle: msg.locationTitle,
		locationAddress: msg.locationAddress,
		locationLatitude: msg.locationLatitude,
		locationLongitude: msg.locationLongitude,
		flexAltText: msg.flexAltText,
		flexContents: msg.flexContents as Record<string, unknown> | undefined,
	}))

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>{response.name}</DialogTitle>
				</DialogHeader>

				<ScrollArea className="max-h-[400px]">
					<MessageListPreview messages={messages} />
				</ScrollArea>

				{onSend && (
					<DialogFooter>
						<Button variant="outline" onClick={() => onOpenChange(false)}>
							ปิด
						</Button>
						<Button onClick={onSend}>
							<Pencil className="mr-2 h-4 w-4" />
							แก้ไขก่อนส่ง
						</Button>
					</DialogFooter>
				)}
			</DialogContent>
		</Dialog>
	)
}

// ============================================================================
// Edit Dialog Component (editable before send)
// ============================================================================

interface QuickResponseEditDialogProps {
	response: QuickResponseResponse | null
	open: boolean
	onOpenChange: (open: boolean) => void
	onSend: (modifiedResponse: ModifiedQuickResponse) => void
}

function QuickResponseEditDialog({
	response,
	open,
	onOpenChange,
	onSend,
}: QuickResponseEditDialogProps) {
	const [editedMessages, setEditedMessages] = useState<MessageContentInput[]>(
		[],
	)

	// Reset edited messages when response changes
	useEffect(() => {
		if (response) {
			setEditedMessages(
				response.messages.map((msg) => ({
					id: msg.id || crypto.randomUUID(),
					order: msg.order,
					messageType: msg.messageType as ChatMessageType,
					text: msg.text ?? undefined,
					stickerPackageId: msg.stickerPackageId ?? undefined,
					stickerId: msg.stickerId ?? undefined,
					imageUrl: msg.imageUrl ?? undefined,
					videoUrl: msg.videoUrl ?? undefined,
					videoPreviewUrl: msg.videoPreviewUrl ?? undefined,
					audioUrl: msg.audioUrl ?? undefined,
					audioDuration: msg.audioDuration ?? undefined,
					fileUrl: msg.fileUrl ?? undefined,
					fileName: msg.fileName ?? undefined,
					locationTitle: msg.locationTitle ?? undefined,
					locationAddress: msg.locationAddress ?? undefined,
					locationLatitude: msg.locationLatitude ?? undefined,
					locationLongitude: msg.locationLongitude ?? undefined,
					flexAltText: msg.flexAltText ?? undefined,
					flexContents: msg.flexContents as Record<string, unknown> | undefined,
				})),
			)
		}
	}, [response])

	// Check if all messages are valid
	const allMessagesValid =
		editedMessages.length > 0 &&
		editedMessages.every((msg) => validateMessageContent(msg))

	const handleSend = useCallback(() => {
		if (!response || !allMessagesValid) return

		// Create modified response with edited messages
		const modifiedResponse: ModifiedQuickResponse = {
			...response,
			messages: editedMessages.map((msg, index) => ({
				id: msg.id || crypto.randomUUID(),
				order: index,
				messageType: msg.messageType,
				text: msg.text ?? null,
				stickerPackageId: msg.stickerPackageId ?? null,
				stickerId: msg.stickerId ?? null,
				imageUrl: msg.imageUrl ?? null,
				videoUrl: msg.videoUrl ?? null,
				videoPreviewUrl: msg.videoPreviewUrl ?? null,
				audioUrl: msg.audioUrl ?? null,
				audioDuration: msg.audioDuration ?? null,
				fileUrl: msg.fileUrl ?? null,
				fileName: msg.fileName ?? null,
				locationTitle: msg.locationTitle ?? null,
				locationAddress: msg.locationAddress ?? null,
				locationLatitude: msg.locationLatitude ?? null,
				locationLongitude: msg.locationLongitude ?? null,
				flexAltText: msg.flexAltText ?? null,
				flexContents: (msg.flexContents as Record<string, unknown>) ?? null,
			})),
		}

		onSend(modifiedResponse)
	}, [response, editedMessages, allMessagesValid, onSend])

	if (!response) return null

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="flex max-h-[90vh] max-w-2xl flex-col overflow-hidden">
				<DialogHeader className="shrink-0">
					<DialogTitle className="flex items-center gap-2">
						<Pencil className="h-4 w-4" />
						แก้ไขข้อความก่อนส่ง - {response.name}
					</DialogTitle>
				</DialogHeader>

				<div className="min-h-0 flex-1 overflow-y-auto py-4">
					<MessageListEditor
						messages={editedMessages}
						onChange={setEditedMessages}
						maxMessages={5}
					/>
				</div>

				<DialogFooter className="shrink-0 border-t bg-background pt-4">
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						ยกเลิก
					</Button>
					<Button onClick={handleSend} disabled={!allMessagesValid}>
						<Send className="mr-2 h-4 w-4" />
						ส่งข้อความ
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

// ============================================================================
// Quick Responses Manager (for settings page)
// ============================================================================

const quickResponseSchema = z.object({
	name: z.string().min(1, 'กรุณากรอกชื่อ'),
	messages: z
		.array(messageContentSchema)
		.min(1, 'กรุณาเพิ่มอย่างน้อย 1 ข้อความ'),
})

type QuickResponseFormData = z.infer<typeof quickResponseSchema>

export function QuickResponsesManager() {
	const [isDialogOpen, setIsDialogOpen] = useState(false)
	const [editingResponse, setEditingResponse] =
		useState<QuickResponseResponse | null>(null)
	const [previewResponse, setPreviewResponse] =
		useState<QuickResponseResponse | null>(null)

	const { data: responses, isLoading } = useQueryQuickResponses()
	const createMutation = useMutationCreateQuickResponse()
	const updateMutation = useMutationUpdateQuickResponse()
	const deleteMutation = useMutationDeleteQuickResponse()

	const form = useForm<QuickResponseFormData>({
		resolver: zodResolver(quickResponseSchema),
		defaultValues: {
			name: '',
			messages: [
				{
					id: crypto.randomUUID(),
					order: 0,
					messageType: ChatMessageType.TEXT,
				},
			],
		},
	})

	const handleOpenCreate = () => {
		setEditingResponse(null)
		form.reset({
			name: '',
			messages: [
				{
					id: crypto.randomUUID(),
					order: 0,
					messageType: ChatMessageType.TEXT,
				},
			],
		})
		setIsDialogOpen(true)
	}

	const handleOpenEdit = (response: QuickResponseResponse) => {
		setEditingResponse(response)
		form.reset({
			name: response.name,
			messages: response.messages.map((msg, index) => ({
				id: msg.id || crypto.randomUUID(),
				order: index,
				messageType: msg.messageType as ChatMessageType,
				text: msg.text,
				stickerPackageId: msg.stickerPackageId,
				stickerId: msg.stickerId,
				imageUrl: msg.imageUrl,
				videoUrl: msg.videoUrl,
				videoPreviewUrl: msg.videoPreviewUrl,
				audioUrl: msg.audioUrl,
				audioDuration: msg.audioDuration,
				fileUrl: msg.fileUrl,
				fileName: msg.fileName,
				locationTitle: msg.locationTitle,
				locationAddress: msg.locationAddress,
				locationLatitude: msg.locationLatitude,
				locationLongitude: msg.locationLongitude,
				flexAltText: msg.flexAltText,
				flexContents: msg.flexContents as Record<string, unknown> | undefined,
			})),
		})
		setIsDialogOpen(true)
	}

	const handleSubmit = (data: QuickResponseFormData) => {
		// Map form data to API format
		const messagesPayload = data.messages.map((msg, index) => ({
			messageType: msg.messageType,
			order: index,
			text: msg.text,
			stickerPackageId: msg.stickerPackageId,
			stickerId: msg.stickerId,
			imageUrl: msg.imageUrl,
			videoUrl: msg.videoUrl,
			videoPreviewUrl: msg.videoPreviewUrl,
			audioUrl: msg.audioUrl,
			audioDuration: msg.audioDuration,
			fileUrl: msg.fileUrl,
			fileName: msg.fileName,
			locationTitle: msg.locationTitle,
			locationAddress: msg.locationAddress,
			locationLatitude: msg.locationLatitude,
			locationLongitude: msg.locationLongitude,
			flexAltText: msg.flexAltText,
			flexContents: msg.flexContents as Record<string, never> | undefined,
		}))

		if (editingResponse) {
			updateMutation.mutate(
				{
					id: editingResponse.id,
					data: {
						name: data.name,
						messages: messagesPayload,
					},
				},
				{
					onSuccess: () => {
						setIsDialogOpen(false)
						setEditingResponse(null)
					},
				},
			)
		} else {
			createMutation.mutate(
				{
					name: data.name,
					order: 0,
					messages: messagesPayload,
				},
				{
					onSuccess: () => {
						setIsDialogOpen(false)
					},
				},
			)
		}
	}

	const handleDelete = (id: string) => {
		if (confirm('ต้องการลบข้อความด่วนนี้หรือไม่?')) {
			deleteMutation.mutate(id)
		}
	}

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<div>
					<h3 className="text-lg font-medium">ข้อความด่วน</h3>
					<p className="text-sm text-muted-foreground">
						สร้างข้อความที่ใช้บ่อยเพื่อตอบกลับได้รวดเร็ว (รองรับหลายข้อความ)
					</p>
				</div>
				<Button onClick={handleOpenCreate}>
					<Plus className="mr-2 h-4 w-4" />
					เพิ่มข้อความด่วน
				</Button>
			</div>

			{isLoading ? (
				<div className="space-y-2">
					{Array.from({ length: 3 }).map((_, i) => (
						<Skeleton key={i} className="h-16 w-full" />
					))}
				</div>
			) : !responses || responses.length === 0 ? (
				<div className="rounded-lg border border-dashed p-8 text-center">
					<Zap className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
					<p className="text-sm text-muted-foreground">
						ยังไม่มีข้อความด่วน คลิกปุ่มด้านบนเพื่อเพิ่ม
					</p>
				</div>
			) : (
				<div className="space-y-2">
					{responses.map((response) => (
						<div
							key={response.id}
							className="flex items-center justify-between rounded-lg border p-4"
						>
							<div className="flex-1">
								<p className="font-medium">{response.name}</p>
								<p className="mt-1 text-sm text-muted-foreground">
									{response.messages.length} ข้อความ
								</p>
							</div>
							<div className="flex gap-1">
								<Button
									variant="ghost"
									size="icon"
									onClick={() => setPreviewResponse(response)}
								>
									<Eye className="h-4 w-4" />
								</Button>
								<Button
									variant="ghost"
									size="icon"
									onClick={() => handleOpenEdit(response)}
								>
									<Pencil className="h-4 w-4" />
								</Button>
								<Button
									variant="ghost"
									size="icon"
									onClick={() => handleDelete(response.id)}
								>
									<Trash2 className="h-4 w-4" />
								</Button>
							</div>
						</div>
					))}
				</div>
			)}

			{/* Preview Dialog */}
			<QuickResponsePreviewDialog
				response={previewResponse}
				open={!!previewResponse}
				onOpenChange={(open) => !open && setPreviewResponse(null)}
			/>

			{/* Create/Edit Dialog */}
			<Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
				<DialogContent className="flex max-h-[90vh] max-w-2xl flex-col overflow-hidden">
					<DialogHeader className="shrink-0">
						<DialogTitle>
							{editingResponse ? 'แก้ไขข้อความด่วน' : 'เพิ่มข้อความด่วน'}
						</DialogTitle>
					</DialogHeader>

					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(handleSubmit)}
							className="flex min-h-0 flex-1 flex-col"
						>
							<div className="min-h-0 flex-1 space-y-4 overflow-y-auto py-4">
								<FormField
									control={form.control}
									name="name"
									render={({ field }) => (
										<FormItem>
											<FormLabel>ชื่อ</FormLabel>
											<FormControl>
												<Input placeholder="ชื่อข้อความด่วน" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="messages"
									render={({ field }) => (
										<FormItem>
											<FormLabel>ข้อความ</FormLabel>
											<FormControl>
												<MessageListEditor
													messages={field.value}
													onChange={field.onChange}
													maxMessages={5}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<DialogFooter className="shrink-0 border-t bg-background pt-4">
								<Button
									type="button"
									variant="outline"
									onClick={() => setIsDialogOpen(false)}
								>
									ยกเลิก
								</Button>
								<Button
									type="submit"
									disabled={
										createMutation.isPending || updateMutation.isPending
									}
								>
									{editingResponse ? 'บันทึก' : 'เพิ่ม'}
								</Button>
							</DialogFooter>
						</form>
					</Form>
				</DialogContent>
			</Dialog>
		</div>
	)
}
